import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';
import FocusTrap from 'focus-trap-react';

// Styles
import './tooltip.scss';

// Components
import Button, { BUTTON_STYLES } from '../button/button';
import { ESCAPE_KEY_CODE, ESCAPE_KEY_NAME } from '../../helpers/enums';
import useKeyPress from '../../hooks/use-key-press';
import useOutsideClick from '../../hooks/use-outside-click';

const POSITIONS = {
    top: 'top',
    right: 'right',
    bottom: 'bottom',
    left: 'left'
};

/**
 *
 * @param visibleTexts  Array of strings with texts that will be displayed in the tooltip. Must be plain text.
 * @param position      The position of the tooltip compared to the Info button. Default is bottom.
 * @returns {boolean|*}
 * @constructor
 */
function Tooltip({ visibleTexts, position }) {
    if (!visibleTexts || visibleTexts.length < 1) {
        return false;
    }

    const { i18n } = useLocalisation();

    const [isTooltipVisible, setShowTooltip] = useState(false);
    const showTooltip = () => {
        setShowTooltip(true);
    };

    const closeTooltip = () => {
        setShowTooltip(false);
    };

    const [escapeKeyPressed] = useKeyPress({
        targetKeyName: ESCAPE_KEY_NAME,
        targetKeyCode: ESCAPE_KEY_CODE
    });

    // Effect for handling escape key press
    useEffect(() => {
        if (escapeKeyPressed) {
            closeTooltip();
        }
    }, [escapeKeyPressed]);

    // Click outside
    const tooltipContainerRef = useRef(null);
    useOutsideClick(
        [tooltipContainerRef],
        closeTooltip
    );

    const positionClass = position in POSITIONS ? `tooltip--${position}` : '';

    return (
        <div className="tooltip__container">
            <Button
                buttonStyle={BUTTON_STYLES.iconBackground}
                importedClasses="button--info"
                noTitle
                ariaProps={{ 'aria-label': i18n._(t`Info - Get more info`) }}
                callback={showTooltip}
            />
            {isTooltipVisible ? (
                <div ref={tooltipContainerRef}>
                    <FocusTrap>
                        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                        <div className={`tooltip ${positionClass}`}>
                            <Button
                                buttonStyle={BUTTON_STYLES.iconBackground}
                                importedClasses="button--hidden"
                                callback={closeTooltip}
                            />
                            {
                                visibleTexts.map(visibleText =>
                                    <p key={visibleText} className="tooltip__text">{visibleText}</p>
                                )
                            }
                        </div>
                    </FocusTrap>
                </div>
            ) : ''
            }
        </div>
    );
}

/**
 * Define the default values for the properties being used
 */
Tooltip.defaultProps = {
    position: 'bottom'
};

/**
 * Define the type of properties that can be set on this component
 */
Tooltip.propTypes = {
    visibleTexts: PropTypes.arrayOf(PropTypes.string).isRequired,
    position: PropTypes.string
};

export default Tooltip;



// WEBPACK FOOTER //
// ./components/tooltip/tooltip.js